/**
 * 快捷键工具函数
 */

/**
 * 将keyCode字符串转换为显示名称
 * @param {string} keyCodeStr - 格式如 "13^65" 或 "32"
 * @returns {string} - 显示名称如 "回车+A" 或 "空格"
 */
export function keyCodeToName(keyCodeStr) {
  if (!keyCodeStr || keyCodeStr === '-1') {
    return '未设置';
  }

  const codes = keyCodeStr.split('^');
  const names = [];

  codes.forEach(code => {
    const numCode = parseInt(code);

    if (code === '-1') {
      names.push('未设置');
    } else if (code === 'Ctrl') {
      names.push('Ctrl');
    } else if (code === 'Alt') {
      names.push('Alt');
    } else if (code === 'Shift') {
      names.push('Shift');
    } else if (numCode === 13) {
      names.push('回车');
    } else if (numCode === 32) {
      names.push('空格');
    } else if (numCode === 27) {
      names.push('Esc');
    } else if (numCode === 9) {
      names.push('Tab');
    } else if (numCode === 8) {
      names.push('Backspace');
    } else if (numCode === 46) {
      names.push('Delete');
    } else if (numCode === 36) {
      names.push('Home');
    } else if (numCode === 35) {
      names.push('End');
    } else if (numCode === 33) {
      names.push('PageUp');
    } else if (numCode === 34) {
      names.push('PageDown');
    } else if (numCode === 37) {
      names.push('←');
    } else if (numCode === 38) {
      names.push('↑');
    } else if (numCode === 39) {
      names.push('→');
    } else if (numCode === 40) {
      names.push('↓');
    } else if (numCode >= 65 && numCode <= 90) {
      // A-Z
      names.push(String.fromCharCode(numCode));
    } else if (numCode >= 48 && numCode <= 57) {
      // 0-9
      names.push(String.fromCharCode(numCode));
    } else if (numCode >= 112 && numCode <= 123) {
      // F1-F12
      names.push('F' + (numCode - 111));
    } else if (!isNaN(numCode)) {
      names.push(String.fromCharCode(numCode));
    }
  });

  return names.join('+');
}

/**
 * 检查按键事件是否匹配指定的快捷键
 * @param {KeyboardEvent} event - 键盘事件
 * @param {string} keyCodeStr - 快捷键字符串,格式如 "13^65"
 * @returns {boolean} - 是否匹配
 */
export function matchShortcut(event, keyCodeStr) {
  if (!keyCodeStr || keyCodeStr === '-1') {
    return false;
  }

  const codes = keyCodeStr.split('^');
  const eventKeyCode = event.keyCode;

  // 检查修饰键
  let hasCtrl = false;
  let hasAlt = false;
  let hasShift = false;
  let mainKey = null;

  codes.forEach(code => {
    if (code === 'Ctrl') {
      hasCtrl = true;
    } else if (code === 'Alt') {
      hasAlt = true;
    } else if (code === 'Shift') {
      hasShift = true;
    } else {
      mainKey = parseInt(code);
    }
  });

  // 检查事件是否匹配
  const ctrlMatch = hasCtrl ? (event.ctrlKey || event.metaKey) : !(event.ctrlKey || event.metaKey);
  const altMatch = hasAlt ? event.altKey : !event.altKey;
  const shiftMatch = hasShift ? event.shiftKey : !event.shiftKey;
  const keyMatch = mainKey ? eventKeyCode === mainKey : true;

  return ctrlMatch && altMatch && shiftMatch && keyMatch;
}

/**
 * 获取用户的快捷键设置
 * @param {string} accountNo - 用户账号
 * @param {Array} defaultKeys - 默认快捷键数组
 * @returns {Array} - 快捷键数组
 */
export function getUserKeys(accountNo, defaultKeys) {
  try {
    const keys = localStorage.getItem('key_' + accountNo);
    if (keys) {
      return JSON.parse(keys);
    }
  } catch (e) {
    console.error('获取快捷键设置失败:', e);
  }
  return defaultKeys || [];
}

/**
 * 保存用户的快捷键设置
 * @param {string} accountNo - 用户账号
 * @param {Array} keys - 快捷键数组
 * @returns {boolean} - 是否保存成功
 */
export function saveUserKeys(accountNo, keys) {
  try {
    localStorage.setItem('key_' + accountNo, JSON.stringify(keys));
    return true;
  } catch (e) {
    console.error('保存快捷键设置失败:', e);
    return false;
  }
}

/**
 * 根据快捷键名称查找快捷键配置
 * @param {Array} keys - 快捷键数组
 * @param {string} name - 快捷键名称
 * @returns {Object|null} - 快捷键配置对象
 */
export function findKeyByName(keys, name) {
  return keys.find(key => key.name === name) || null;
}

/**
 * 全局快捷键监听器类
 */
export class ShortcutListener {
  constructor(keys, callbacks) {
    this.keys = keys;
    this.callbacks = callbacks;
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  /**
   * 处理键盘按下事件
   */
  handleKeyDown(event) {
    this.keys.forEach(key => {
      if (matchShortcut(event, key.keyCode)) {
        const callback = this.callbacks[key.name];
        if (callback && typeof callback === 'function') {
          event.preventDefault();
          event.stopPropagation();
          callback(event);
        }
      }
    });
  }

  /**
   * 开始监听
   */
  start() {
    document.addEventListener('keydown', this.handleKeyDown);
  }

  /**
   * 停止监听
   */
  stop() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  /**
   * 更新快捷键配置
   */
  updateKeys(keys) {
    this.keys = keys;
  }
}

export default {
  keyCodeToName,
  matchShortcut,
  getUserKeys,
  saveUserKeys,
  findKeyByName,
  ShortcutListener,
};
